import React, { useState } from 'react';
import { Upload, FileText, Copy, Download, Sparkles, BookOpen, HelpCircle } from 'lucide-react';

function App() {
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock AI summarization function
  const generateSummary = (text) => {
    const wordCount = text.split(/\s+/).length;
    const sentences = text.split(/[.!?]+/).filter(s => s.trim());

    // Generate summary (first 2-3 sentences or key points)
    const summaryText = sentences.slice(0, Math.min(3, sentences.length)).join('. ') + '.';

    // Generate key points
    const keyPoints = sentences.slice(0, 5).map((s, i) => {
      const trimmed = s.trim();
      return trimmed || `Key point ${i + 1} from the text`;
    }).filter(Boolean);

    // Generate quiz questions
    const quizQuestions = [
      {
        question: "What is the main topic discussed in this text?",
        answer: "The text discusses " + (sentences[0] || "various topics").substring(0, 100) + "..."
      },
      {
        question: "What are the key takeaways?",
        answer: keyPoints.slice(0, 2).join(' and ') || "Multiple important points are covered."
      },
      {
        question: "How would you summarize this in one sentence?",
        answer: summaryText.substring(0, 150) + "..."
      }
    ];

    return {
      summary: summaryText,
      keyPoints: keyPoints.slice(0, 5),
      quiz: quizQuestions,
      stats: {
        wordCount,
        sentenceCount: sentences.length,
        estimatedReadTime: Math.ceil(wordCount / 200)
      }
    };
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setInputText(event.target.result);
      };
      reader.readAsText(file);
    } else if (file) {
      alert('Please upload a .txt file');
    }
  };

  const handleSummarize = () => {
    if (!inputText.trim()) {
      alert('Please enter or upload some text first!');
      return;
    }

    setIsProcessing(true);

    // Simulate AI processing delay
    setTimeout(() => {
      const result = generateSummary(inputText);
      setResults(result);
      setIsProcessing(false);
    }, 1500);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleDownload = () => {
    if (!results) return;

    const content = `
# AI Note Summary

## Summary
${results.summary}

## Key Points
${results.keyPoints.map((point, i) => `${i + 1}. ${point}`).join('\n')}

## Quiz Questions
${results.quiz.map((q, i) => `
Q${i + 1}: ${q.question}
A: ${q.answer}
`).join('\n')}

## Statistics
- Word Count: ${results.stats.wordCount}
- Sentences: ${results.stats.sentenceCount}
- Estimated Read Time: ${results.stats.estimatedReadTime} min
    `.trim();

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'note-summary.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-dark-bg p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="text-accent-blue" size={40} />
            <h1 className="text-5xl font-poppins font-bold bg-gradient-primary bg-clip-text text-transparent">
              AI Note Summarizer
            </h1>
          </div>
          <p className="text-white/60 text-lg">
            Transform any text into concise summaries, key points, and quiz questions
          </p>
        </header>

        {/* Input Section */}
        <div className="glass-card p-8 mb-8">
          <h2 className="text-2xl font-poppins font-semibold mb-6 flex items-center gap-2">
            <FileText className="text-accent-blue" size={28} />
            Input Your Text
          </h2>

          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Paste your lesson notes, article, transcript, or any text here..."
            className="textarea-field h-64 mb-4"
          />

          <div className="flex flex-col sm:flex-row gap-4">
            <label className="btn-secondary flex items-center gap-2 cursor-pointer flex-1 justify-center">
              <Upload size={20} />
              Upload .txt File
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <button
              onClick={handleSummarize}
              disabled={isProcessing}
              className={`btn-primary flex items-center gap-2 flex-1 justify-center ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Sparkles size={20} className={isProcessing ? 'animate-spin' : ''} />
              {isProcessing ? 'Processing...' : 'Summarize with AI'}
            </button>
          </div>

          {inputText && (
            <div className="mt-4 text-sm text-white/50">
              {inputText.split(/\s+/).length} words • {Math.ceil(inputText.split(/\s+/).length / 200)} min read
            </div>
          )}
        </div>

        {/* Results Section */}
        {results && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-poppins font-semibold">Results</h2>
              <button
                onClick={handleDownload}
                className="btn-secondary flex items-center gap-2"
              >
                <Download size={18} />
                Download as .md
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Summary Card */}
              <div className="glass-card p-6 lg:col-span-2">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-poppins font-semibold flex items-center gap-2">
                    <BookOpen className="text-accent-blue" size={24} />
                    Summary
                  </h3>
                  <button
                    onClick={() => handleCopy(results.summary)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Copy summary"
                  >
                    <Copy size={18} className="text-white/60" />
                  </button>
                </div>
                <p className="text-white/80 leading-relaxed">{results.summary}</p>
                <div className="mt-4 flex gap-4 text-sm text-white/50">
                  <span>{results.stats.wordCount} words</span>
                  <span>•</span>
                  <span>{results.stats.sentenceCount} sentences</span>
                  <span>•</span>
                  <span>{results.stats.estimatedReadTime} min read</span>
                </div>
              </div>

              {/* Key Points Card */}
              <div className="glass-card p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-poppins font-semibold flex items-center gap-2">
                    <Sparkles className="text-accent-purple" size={24} />
                    Key Points
                  </h3>
                  <button
                    onClick={() => handleCopy(results.keyPoints.join('\n'))}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Copy key points"
                  >
                    <Copy size={18} className="text-white/60" />
                  </button>
                </div>
                <ul className="space-y-3">
                  {results.keyPoints.map((point, index) => (
                    <li key={index} className="flex gap-3 text-white/80">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-purple/20 text-accent-purple flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quiz Card */}
              <div className="glass-card p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-poppins font-semibold flex items-center gap-2">
                    <HelpCircle className="text-green-400" size={24} />
                    Quiz Questions
                  </h3>
                  <button
                    onClick={() => handleCopy(results.quiz.map((q, i) => `Q${i + 1}: ${q.question}\nA: ${q.answer}`).join('\n\n'))}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Copy quiz"
                  >
                    <Copy size={18} className="text-white/60" />
                  </button>
                </div>
                <div className="space-y-4">
                  {results.quiz.map((item, index) => (
                    <div key={index} className="border-l-2 border-green-400/30 pl-4">
                      <p className="font-semibold text-white mb-2">Q{index + 1}: {item.question}</p>
                      <p className="text-white/60 text-sm">A: {item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!results && !isProcessing && (
          <div className="glass-card p-12 text-center">
            <Sparkles className="mx-auto mb-4 text-white/30" size={64} />
            <h3 className="text-xl font-semibold text-white/70 mb-2">
              Ready to Summarize
            </h3>
            <p className="text-white/50">
              Paste or upload your text above and click "Summarize with AI" to get started
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
